import Sidebar, {
  SidebarLink,
  SIDEBAR_EXPANDED,
  SIDEBAR_COLLAPSED,
} from "./desktopSidebar";
import { Link, useLocation, Navigate, useNavigate } from "@tanstack/react-router";
import {
  House,
  Calendar,
  MessageSquare,
  Table2,
  BookOpen,
  Settings,
  Route,
  Users,
  Briefcase,
  Building2,
  FileText,
  BarChart3,
  Layers,
  BadgeCheck,
  CreditCard,
  AlertTriangle,
  FileBadge,
} from "lucide-react";
import { useAuth } from "@clerk/clerk-react";
import SessionLoading from "../components/SessionLoading";
import { useSelector } from "react-redux";
import { useState, useEffect, useRef, useMemo } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../../@/components/ui/accordion";

export const instructorNavSections = [
  {
    section: null,
    items: [{ title: "My Dashboard", path: "/", icon: House }],
  },
  {
    section: "Tracking",
    items: [
      { title: "Manage Classes", path: "/live-classes", icon: Calendar },
      { title: "Review Assignments", path: "/review-projects", icon: MessageSquare },
      { title: "Track My Students", path: "/track-students", icon: Users },
    ],
  },
  {
    section: "Courses",
    items: [
      { title: "Manage Courses", path: "/manage-courses", icon: BookOpen },
      { title: "Course Analytics", path: "/course-analytics", icon: BarChart3 },
      { title: "Course Extras", path: "/course-extras", icon: Layers },
    ],
  },
];

// Admin Mode (Admin-only)
export const adminModeNavSections = [
  {
    section: null,
    items: [
      { title: "Admin Dashboard", path: "/", icon: House },
      // { title: "Overall Analytics", path: "/overall-analytics", icon: BarChart3 },
    ],
  },
  {
    section: "Operations",
    items: [
      {
        title: "Manage Pathways",
        icon: Route,
        items: [
          { title: "All Enrolments", path: "/manage-pathways?tab=enrolment", icon: Users },
          { title: "Add/Edit Pathways", path: "/manage-pathways?tab=pathways", icon: Route },
          { title: "Add/Edit Pathway Types", path: "/manage-pathways?tab=pathway-types", icon: Layers },
        ],
      },
      {
        title: "Manage Cohorts",
        icon: Users,
        items: [
          { title: "Current Enrolments", path: "/track-students?tab=active-students", icon: Users },
          { title: "All Enrolments", path: "/track-students?tab=all-students", icon: Users },
          { title: "Add/Edit Cohorts", path: "/track-students?tab=cohorts", icon: Settings },
        ],
      },
      {
        title: "Billing/Finance",
        path: "/manage-billing",
        icon: Table2,
      },
      {
        title: "More Operations",
        icon: Settings,
        items: [
          { title: "Manage Certificates", path: "/manage-certificates", icon: BadgeCheck },
          { title: "Global Taxonomies", path: "/global-taxonomies", icon: Layers },
        ],
      },
    ],
  },
  {
    section: "Master View",
    items: [
      {
        title: "Course Master",
        icon: BookOpen,
        items: [
          { title: "All Courses", path: "/manage-courses", icon: BookOpen },
          { title: "All Course Analytics", path: "/course-analytics", icon: BarChart3 },
          { title: "All Course Extras", path: "/course-extras", icon: Layers },
        ],
      },
      {
        title: "Tracking Master",
        icon: Table2,
        items: [
          // Open Upcoming Classes by default
          { title: "All Classes", path: "/live-classes?tab=upcoming", icon: Calendar },
          { title: "All Assignments", path: "/review-projects", icon: MessageSquare },
        ],
      },
      // {
      //   title: "Jobs Board Master",
      //   icon: Briefcase,
      //   items: [
      //     { title: "All Jobs/ Opportunities", path: "/job-board/jobs", icon: Briefcase },
      //     { title: "All Companies", path: "/job-board/companies", icon: Building2 },
      //     { title: "Applications", path: "/job-board/applications", icon: FileBadge },
      //   ],
      // },
    ],
  },
  {
    section: "Admin Zone",
    items: [
      { title: "User Management", path: "/admin-portal", icon: Users },
      // { title: "Org Management", path: "/org-management", icon: Building2 },
    ],
  },
];

// Managers: replica of admin sidebar hierarchy, except:
// - No Admin Mode toggle (handled by MainSidebar)
// - No Admin Zone section
// - Dashboard label is "My Dashboard"
export const managerNavSections = [
  {
    section: null,
    items: [
      { title: "My Dashboard", path: "/", icon: House },
      // { title: "Overall Analytics", path: "/overall-analytics", icon: BarChart3 },
    ],
  },
  {
    section: "Operations",
    items: [
      {
        title: "Manage Pathways",
        icon: Route,
        items: [
          { title: "All Enrolments", path: "/manage-pathways?tab=enrolment", icon: Users },
          { title: "Add/Edit Pathways", path: "/manage-pathways?tab=pathways", icon: Route },
          { title: "Add/Edit Pathway Types", path: "/manage-pathways?tab=pathway-types", icon: Layers },
        ],
      },
      {
        title: "Manage Cohorts",
        icon: Users,
        items: [
          { title: "Current Enrolments", path: "/track-students?tab=active-students", icon: Users },
          { title: "All Enrolments", path: "/track-students?tab=all-students", icon: Users },
          { title: "Add/Edit Cohorts", path: "/track-students?tab=cohorts", icon: Settings },
        ],
      },
      {
        title: "Billing/Finance",
        path: "/manage-billing",
        icon: Table2,
      },
      {
        title: "More Operations",
        icon: Settings,
        items: [
          { title: "Manage Certificates", path: "/manage-certificates", icon: BadgeCheck },
          { title: "Global Taxonomies", path: "/global-taxonomies", icon: Layers },
        ],
      },
    ],
  },
  {
    section: "Master View",
    items: [
      {
        title: "Course Master",
        icon: BookOpen,
        items: [
          { title: "Manage Courses", path: "/manage-courses", icon: BookOpen },
          { title: "Course Analytics", path: "/course-analytics", icon: BarChart3 },
          { title: "Course Extras", path: "/course-extras", icon: Layers },
        ],
      },
      {
        title: "Tracking Master",
        icon: Table2,
        items: [
          { title: "All Classes", path: "/live-classes?tab=upcoming", icon: Calendar },
          { title: "All Assignments", path: "/review-projects", icon: MessageSquare },
        ],
      },
      {
        title: "Jobs Board Master",
        icon: Briefcase,
        items: [
          { title: "All Jobs/ Opportunities", path: "/job-board/jobs", icon: Briefcase },
          { title: "All Companies", path: "/job-board/companies", icon: Building2 },
          { title: "Applications", path: "/job-board/applications", icon: FileBadge },
        ],
      },
    ],
  },
];

const moreMenuItems = [];

const MainSidebar = ({ children }) => {
  const [expanded, setExpanded] = useState(window.innerWidth > 766);
  const [managerMode, setManagerMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("zindua_manager_mode") === "on";
  });
  const [adminMode, setAdminMode] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem("zindua_admin_mode") === "on";
  });
  const { isLoaded, isSignedIn } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const roles = useSelector((s) => s.auth.roles) || [];
  const isAdmin = roles.includes("Admin");
  const isManager = roles.includes("Manager");

  const prevAdminModeRef = useRef(adminMode);

  const instructorAllowedPathnames = useMemo(() => {
    const pathnames = new Set();

    const addPath = (rawPath) => {
      if (!rawPath || typeof rawPath !== "string") return;
      const pathname = rawPath.split("?")[0];
      if (pathname) pathnames.add(pathname);
    };

    for (const section of instructorNavSections || []) {
      if (Array.isArray(section?.items)) {
        section.items.forEach((it) => addPath(it?.path));
      } else {
        addPath(section?.path);
      }
    }

    // Always allow dashboard.
    pathnames.add("/");
    return pathnames;
  }, []);

  useEffect(() => {
    const handleResize = () => setExpanded(window.innerWidth > 766);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Only Admins can have adminMode enabled.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (isAdmin) return;
    if (!adminMode) return;
    setAdminMode(false);
    try {
      window.localStorage.setItem("zindua_admin_mode", "off");
      window.dispatchEvent(new Event("zindua-mode-changed"));
    } catch {
      // ignore
    }
  }, [adminMode, isAdmin, isLoaded, isSignedIn]);

  // Only Managers can have managerMode enabled (and Managers never override Admins).
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (isAdmin) {
      if (!managerMode) return;
      setManagerMode(false);
      try {
        window.localStorage.setItem("zindua_manager_mode", "off");
        window.dispatchEvent(new Event("zindua-mode-changed"));
      } catch {
        // ignore
      }
      return;
    }
    if (isManager) return;
    if (!managerMode) return;
    setManagerMode(false);
    try {
      window.localStorage.setItem("zindua_manager_mode", "off");
      window.dispatchEvent(new Event("zindua-mode-changed"));
    } catch {
      // ignore
    }
  }, [managerMode, isAdmin, isManager, isLoaded, isSignedIn]);

  // If a user turns admin mode OFF, only redirect if their current page
  // isn't available in instructor mode.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!isAdmin) return;
    const wasOn = Boolean(prevAdminModeRef.current);
    if (wasOn && !adminMode) {
      const currentPathname = location?.pathname || "/";
      if (!instructorAllowedPathnames.has(currentPathname)) {
        navigate({ to: "/" });
      }
    }
    prevAdminModeRef.current = adminMode;
  }, [adminMode, navigate, isAdmin, isLoaded, isSignedIn, location?.pathname, instructorAllowedPathnames]);

  if (!isLoaded) {
    return <SessionLoading />;
  }
  if (!isSignedIn) return <Navigate to="/sign-in" replace />;

  const toggleAdminMode = () => {
    if (!isAdmin) return;
    setAdminMode((prev) => !prev);
  };

  const toggleManagerMode = () => {
    if (isAdmin) return;
    if (!isManager) return;
    setManagerMode((prev) => !prev);
  };

  // Persist mode toggles (and notify listeners) outside of setState updaters.
  // This avoids "Cannot update a component while rendering a different component" warnings,
  // because state updaters can be invoked during render in React.
  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (!isAdmin) return;
    try {
      window.localStorage.setItem("zindua_admin_mode", adminMode ? "on" : "off");
      window.dispatchEvent(new Event("zindua-mode-changed"));
    } catch {
      // ignore
    }
  }, [adminMode, isAdmin, isLoaded, isSignedIn]);

  useEffect(() => {
    if (!isLoaded || !isSignedIn) return;
    if (isAdmin) return;
    if (!isManager) return;
    try {
      window.localStorage.setItem("zindua_manager_mode", managerMode ? "on" : "off");
      window.dispatchEvent(new Event("zindua-mode-changed"));
    } catch {
      // ignore
    }
  }, [isAdmin, isLoaded, isManager, isSignedIn, managerMode]);

  // Role-driven navigation:
  // - Everyone defaults to Instructor nav
  // - Admins can toggle Admin Mode to see Admin nav
  // - Managers can toggle Manager Mode to see Manager nav
  const navSections = (() => {
    if (isAdmin) return adminMode ? adminModeNavSections : instructorNavSections;
    if (isManager) return managerMode ? managerNavSections : instructorNavSections;
    return instructorNavSections;
  })();

  const sidebarModeEnabled = isAdmin ? adminMode : isManager ? managerMode : false;
  const showModeToggle = isAdmin || (!isAdmin && isManager);
  const onToggleMode = isAdmin ? toggleAdminMode : isManager ? toggleManagerMode : undefined;
  const modeLabel = isAdmin ? "Admin" : "Manager";

  const sidebarWidth = expanded ? SIDEBAR_EXPANDED : SIDEBAR_COLLAPSED;

  return (
    <div
      className="flex min-h-screen max-w-screen"
      style={{ maxWidth: "100vw" }}
    >
      {/* Sidebar: fixed on left */}
      <Sidebar
        expanded={expanded}
        setExpanded={setExpanded}
        moreMenu={moreMenuItems}
        adminMode={sidebarModeEnabled}
        showAdminToggle={showModeToggle}
        onToggleAdminMode={onToggleMode}
        modeLabel={modeLabel}
      >
        <Accordion type="multiple" className="w-full">
          {navSections.map((section) => (
            <div key={section.section || "root"} className="w-full">
              {expanded && section.section && (
                <div className="px-3 pt-2 pb-1 text-[11px] font-heading text-(--neutral-500)">
                  {section.section}
                </div>
              )}

              <div className="flex flex-col gap-0.5">
                {section.items.map((item) => {
                  const isGroup = Array.isArray(item.items);
                  const groupKey = `group:${section.section || "root"}:${item.title}`;

                  if (isGroup) {
                    const currentFullPath = `${location.pathname}${location.search || ""}`;
                    const isGroupActive = item.items?.some((sub) => {
                      const target = sub?.path;
                      if (!target) return false;
                      if (target.includes("?")) return currentFullPath === target;
                      return location.pathname === target;
                    });
                    const Icon = item.icon;

                    return (
                      <AccordionItem key={groupKey} value={groupKey} className="border-0">
                        <AccordionTrigger
                          className={
                            [
                              // Match normal SidebarLink: background is inset (doesn't include the outer px),
                              // but still covers the chevron since it's inside the trigger.
                              "relative px-1.5 py-0 hover:no-underline items-center [&>svg]:text-(--neutral-500)",
                              "before:content-[''] before:absolute before:inset-y-0 before:left-1.5 before:right-1.5 before:rounded-[10px] before:transition-colors before:duration-200",
                              "*:relative *:z-10",
                              isGroupActive
                                ? "before:bg-(--neutral-100) text-(--color-primary)"
                                : "text-(--neutral-900) hover:before:bg-(--neutral-100) hover:text-(--color-primary)",
                              !expanded && "justify-center [&>svg]:hidden",
                            ]
                              .filter(Boolean)
                              .join(" ")
                          }
                        >
                          <span
                            className={
                              [
                                "inline-flex items-center w-full pl-2.5 pr-2 py-1 transition-all duration-200 ease-out",
                                !expanded && "justify-center w-auto mx-0 pl-0 pr-0",
                                "text-[14px]",
                              ].join(" ")
                            }
                          >
                            {Icon ? <Icon className="w-5 h-5" /> : null}
                            {expanded && (
                              <span className="ml-3 flex-1 text-left">{item.title}</span>
                            )}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="pt-1 pb-1">
                          <div
                            className={
                              expanded
                                ? "ml-6 flex flex-col gap-0.5"
                                : "flex flex-col gap-0.5 px-1"
                            }
                          >
                            {item.items.map((sub) => (
                              <SidebarLink
                                key={`${groupKey}:${sub.path}`}
                                as={Link}
                                to={sub.path}
                                expanded={expanded}
                                tooltip={sub.title}
                                isSubLink
                              >
                                {!expanded && (() => {
                                  const SubIcon = sub.icon || FileText;
                                  return <SubIcon className="w-4 h-4" />;
                                })()}
                                <span className={expanded ? "ml-3" : "ml-0"}>
                                  {sub.title}
                                </span>
                              </SidebarLink>
                            ))}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    );
                  }

                  const Icon = item.icon;
                  return (
                    <SidebarLink
                      key={item.path}
                      as={Link}
                      to={item.path}
                      expanded={expanded}
                      tooltip={item.title}
                    >
                      {Icon ? <Icon className="w-5 h-5" /> : null}
                      <span className="ml-3">{item.title}</span>
                    </SidebarLink>
                  );
                })}
              </div>
            </div>
          ))}
        </Accordion>
      </Sidebar>

      {/* Main content: margin-left matches sidebar width */}
      <main
        className="flex flex-col min-h-screen transition-all duration-300 relative z-10 pt-0 overflow-x-hidden"
        style={{
          marginLeft: window.innerWidth > 766 ? `${sidebarWidth}px` : 0,
          width:
            window.innerWidth > 766
              ? `calc(100vw - ${sidebarWidth}px)`
              : "100vw",
          transition:
            "margin-left 0.3s cubic-bezier(0.4,0,0.2,1), width 0.3s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        <div className="w-full max-w-325 mx-auto grow px-4 py-4 md:px-5 md:py-5 min-w-0 ">
          {children}
        </div>
      </main>
    </div>
  );
};

export default MainSidebar;
